/**
 * EnvironmentsPicker — one list of every place a user can open a thread:
 * cloud sprites beside their own desktop and local machines (forge#3520).
 *
 * CMMD unions the two registries server-side and discriminates rows with
 * `kind`; this component renders that payload and owns nothing about where it
 * came from — the surface that mounts it (today Settings > Connections) hands
 * the snapshot down as props. Loading renders skeletons, an error renders the
 * retry the surface provides, and an empty list renders `emptyMessage`.
 */

import { CloudIcon, HardDriveIcon, MonitorIcon } from "lucide-react";
import type { ReactElement } from "react";

import type {
  CmmdEnvironmentKind,
  CmmdEnvironmentPickerEntry,
} from "../../environments/cmmdEnvironments/cmmdEnvironmentListTypes";
import { Skeleton } from "../ui/skeleton";
import {
  environmentDotClass,
  environmentKindLabel,
  environmentStatusLabel,
  formatRelativeLastSeen,
} from "./environmentsPickerState";

const KIND_ICONS: Readonly<Record<CmmdEnvironmentKind, typeof CloudIcon>> = {
  "cloud-sprite": CloudIcon,
  desktop: MonitorIcon,
  local: HardDriveIcon,
};

export interface EnvironmentsPickerProps {
  readonly environments: readonly CmmdEnvironmentPickerEntry[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly selectedId?: string | null;
  readonly onSelect?: (entry: CmmdEnvironmentPickerEntry) => void;
  readonly emptyMessage?: string;
}

/** The row's secondary line: the sprite backing a cloud environment, or the
 * device binding for the user's own machines. */
function environmentSubLabel(entry: CmmdEnvironmentPickerEntry): string | null {
  if (entry.kind === "cloud-sprite") {
    return entry.spriteName ?? null;
  }
  return null;
}

function EnvironmentsPickerRow({
  entry,
  selected,
  onSelect,
}: {
  readonly entry: CmmdEnvironmentPickerEntry;
  readonly selected: boolean;
  readonly onSelect?: (entry: CmmdEnvironmentPickerEntry) => void;
}) {
  const Icon = KIND_ICONS[entry.kind];
  const subLabel = environmentSubLabel(entry);
  const lastSeenLabel = formatRelativeLastSeen(entry.lastSeenAt, Date.now());
  const body = (
    <>
      <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-col items-start gap-0.5">
        <span className="w-full truncate text-sm font-medium text-foreground">{entry.label}</span>
        <span className="truncate text-xs text-muted-foreground">
          {environmentKindLabel(entry.kind)}
          {subLabel ? <span className="text-muted-foreground/70"> · {subLabel}</span> : null}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2 justify-self-end">
        <span className="text-xs text-muted-foreground">{environmentStatusLabel(entry)}</span>
        {lastSeenLabel ? (
          <span className="text-xs text-muted-foreground/70">{lastSeenLabel}</span>
        ) : null}
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${environmentDotClass(entry)}`}
        />
      </span>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(entry)}
        aria-pressed={selected}
        data-testid={`environment-picker-row-${entry.kind}`}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {body}
      </button>
    );
  }
  return (
    <div
      data-testid={`environment-picker-row-${entry.kind}`}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-3 py-2.5"
    >
      {body}
    </div>
  );
}

function EnvironmentsPickerSkeletonRows() {
  return (
    <div className="space-y-2" aria-hidden="true">
      <Skeleton className="h-14 w-full rounded-lg" />
      <Skeleton className="h-14 w-full rounded-lg" />
    </div>
  );
}

export function EnvironmentsPicker({
  environments,
  isLoading,
  error,
  selectedId = null,
  onSelect,
  emptyMessage = "No environments yet.",
}: EnvironmentsPickerProps): ReactElement {
  return (
    <div className="space-y-1">
      {isLoading ? (
        <EnvironmentsPickerSkeletonRows />
      ) : error !== null && environments.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground" role="alert">
          {error}
        </p>
      ) : environments.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        environments.map((entry) => (
          <EnvironmentsPickerRow
            key={`${entry.kind}:${entry.id}`}
            entry={entry}
            selected={selectedId !== null && entry.id === selectedId}
            {...(onSelect ? { onSelect } : {})}
          />
        ))
      )}
    </div>
  );
}
